import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminContacts, saveContact, deleteContact, type ContactPayload } from "@/services/adminService";
import type { CrmContact, CrmStage } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import { useAdminCounters } from "@/pages/portal/admin/context/AdminCountersContext";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import ContactDrawer from "@/pages/portal/admin/components/ContactDrawer";
import {
  adminCard, adminTh, adminTd, adminBtnPrimary, adminBtnGhost, adminBtnDanger, adminIconBtn, adminInput, adminLabel,
} from "@/pages/portal/admin/config/ui";
import { downloadCsv } from "@/utils/csv";

const STAGES: { key: CrmStage; label: string }[] = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal", label: "Proposal" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

const STAGE_BADGE: Record<CrmStage, string> = {
  new: "bg-secondary-100 text-secondary-800",
  contacted: "bg-accent-100 text-accent-800",
  qualified: "bg-primary-100 text-primary-700",
  proposal: "bg-amber-100 text-amber-800",
  won: "bg-emerald-100 text-emerald-800",
  lost: "bg-rose-100 text-rose-700",
};

const fmt = (n: number) => n.toLocaleString("en-IN");
const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const EMPTY: ContactPayload = {
  name: "", company: "", email: "", phone: "", stage: "new", value: 0, owner: "Platform Admin", source: "Website form",
};

export default function ContactsSection() {
  const { accessToken } = useAuth();
  const { refresh: refreshCounters } = useAdminCounters();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminContacts(accessToken),
    [] as CrmContact[],
    [accessToken],
  );
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState<"all" | CrmStage>("all");
  const [form, setForm] = useState<ContactPayload | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CrmContact | null>(null);
  const [active, setActive] = useState<CrmContact | null>(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((c) => {
      const matchStage = stage === "all" || c.stage === stage;
      const matchQ = !q || [c.name, c.company, c.email, c.source].some((v) => v.toLowerCase().includes(q));
      return matchStage && matchQ;
    });
  }, [data, search, stage]);

  function openCreate() {
    setEditId(null);
    setForm({ ...EMPTY });
  }

  function openEdit(c: CrmContact) {
    setActive(null);
    setEditId(c.id);
    setForm({ name: c.name, company: c.company, email: c.email, phone: c.phone ?? "", stage: c.stage, value: c.value, owner: c.owner, source: c.source });
  }

  async function submit() {
    if (!form?.name?.trim()) return;
    setBusy(true);
    try {
      await saveContact(accessToken, form, editId ?? undefined);
      setForm(null);
      setEditId(null);
      await reload();
      refreshCounters();
    } catch {
      // keep modal open on failure
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteContact(accessToken, deleteTarget.id);
      setData((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      refreshCounters();
    } catch {
      // keep modal open on failure
    } finally {
      setBusy(false);
    }
  }

  function exportCsv() {
    downloadCsv(
      `crm-contacts-${new Date().toISOString().slice(0, 10)}`,
      ["Name", "Company", "Email", "Phone", "Stage", "Deal Value", "Owner", "Source", "Last Activity", "Created"],
      filtered.map((c) => [c.name, c.company, c.email, c.phone ?? "", c.stage, c.value, c.owner, c.source, fmtDate(c.lastActivity), fmtDate(c.createdAt)]),
    );
  }

  const set = <K extends keyof ContactPayload>(key: K, value: ContactPayload[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <AdminSearchInput value={search} onChange={setSearch} placeholder="Search contacts, companies…" />
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value as "all" | CrmStage)}
            className={`${adminInput} h-10 w-full cursor-pointer sm:w-40`}
            aria-label="Filter by stage"
          >
            <option value="all">All stages</option>
            {STAGES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || filtered.length === 0}
            className={`${adminBtnGhost} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <i className="ri-download-2-line text-base" /> Export CSV
          </button>
          <button type="button" onClick={openCreate} className={adminBtnPrimary}>
            <i className="ri-add-line text-base" /> Add contact
          </button>
        </div>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse">
            <thead className="border-b border-background-200 bg-background-100/60">
              <tr>
                <th className={adminTh}>Contact</th>
                <th className={adminTh}>Company</th>
                <th className={adminTh}>Stage</th>
                <th className={adminTh}>Deal value</th>
                <th className={adminTh}>Source</th>
                <th className={adminTh}>Last activity</th>
                <th className={`${adminTh} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading ? (
                <tr><td colSpan={7} className="py-14 text-center"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="py-14 text-center text-sm text-foreground-500">No contacts match your filters.</td></tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-background-100/50">
                    <td className={adminTd}>
                      <button
                        type="button"
                        onClick={() => setActive(c)}
                        className="cursor-pointer text-left font-semibold text-foreground-900 transition-colors hover:text-primary-600"
                      >
                        {c.name}
                      </button>
                      <p className="text-xs text-foreground-500">{c.email}</p>
                    </td>
                    <td className={adminTd}>{c.company}</td>
                    <td className={adminTd}>
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${STAGE_BADGE[c.stage]}`}>
                        {c.stage}
                      </span>
                    </td>
                    <td className={`${adminTd} tabular-nums font-semibold text-foreground-900`}>₹{fmt(c.value)}</td>
                    <td className={adminTd}>{c.source}</td>
                    <td className={`${adminTd} text-xs text-foreground-500`}>{fmtDate(c.lastActivity)}</td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex justify-end gap-1">
                        <button type="button" onClick={() => setActive(c)} className={adminIconBtn} title="View contact">
                          <i className="ri-eye-line text-base" />
                        </button>
                        <button type="button" onClick={() => openEdit(c)} className={adminIconBtn} title="Edit contact">
                          <i className="ri-pencil-line text-base" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(c)} className={adminIconBtn} title="Delete contact">
                          <i className="ri-delete-bin-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {form !== null && (
      <AdminModal
        onClose={() => { setForm(null); setEditId(null); }}
        title={editId ? "Edit contact" : "Add contact"}
        footer={
          <>
            <button type="button" onClick={() => { setForm(null); setEditId(null); }} className={adminBtnGhost}>Cancel</button>
            <button type="button" disabled={!form?.name?.trim() || busy} onClick={submit} className={adminBtnPrimary}>
              {busy ? "Saving…" : editId ? "Save changes" : "Add contact"}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={adminLabel} htmlFor="ct-name">Full name</label>
            <input id="ct-name" type="text" value={form?.name ?? ""} onChange={(e) => set("name", e.target.value)} className={adminInput} />
          </div>
          <div>
            <label className={adminLabel} htmlFor="ct-company">Company</label>
            <input id="ct-company" type="text" value={form?.company ?? ""} onChange={(e) => set("company", e.target.value)} className={adminInput} />
          </div>
          <div>
            <label className={adminLabel} htmlFor="ct-email">Email</label>
            <input id="ct-email" type="email" value={form?.email ?? ""} onChange={(e) => set("email", e.target.value)} className={adminInput} />
          </div>
          <div>
            <label className={adminLabel} htmlFor="ct-phone">Phone</label>
            <input id="ct-phone" type="tel" value={form?.phone ?? ""} onChange={(e) => set("phone", e.target.value)} className={adminInput} />
          </div>
          <div>
            <label className={adminLabel} htmlFor="ct-stage">Stage</label>
            <select id="ct-stage" value={form?.stage ?? "new"} onChange={(e) => set("stage", e.target.value as CrmStage)} className={`${adminInput} cursor-pointer`}>
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className={adminLabel} htmlFor="ct-value">Deal value (₹)</label>
            <input id="ct-value" type="number" min={0} value={form?.value ?? 0} onChange={(e) => set("value", Number(e.target.value) || 0)} className={adminInput} />
          </div>
          <div>
            <label className={adminLabel} htmlFor="ct-owner">Owner</label>
            <input id="ct-owner" type="text" value={form?.owner ?? ""} onChange={(e) => set("owner", e.target.value)} className={adminInput} />
          </div>
          <div>
            <label className={adminLabel} htmlFor="ct-source">Source</label>
            <input id="ct-source" type="text" value={form?.source ?? ""} onChange={(e) => set("source", e.target.value)} className={adminInput} />
          </div>
        </div>
      </AdminModal>
      )}

      {deleteTarget !== null && (
      <AdminModal
        onClose={() => setDeleteTarget(null)}
        title="Delete contact"
        footer={
          <>
            <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
            <button type="button" disabled={busy} onClick={confirmDelete} className={adminBtnDanger}>
              {busy ? "Deleting…" : "Delete contact"}
            </button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          Remove <strong>{deleteTarget?.name}</strong> from your CRM? This cannot be undone.
        </p>
      </AdminModal>
      )}

      {active !== null && (
        <ContactDrawer contact={active} onClose={() => setActive(null)} onEdit={openEdit} />
      )}
    </div>
  );
}